import { cn } from '@/lib/utils';
import type { WorkoutAttribution } from '@ergshoot/shared';

export interface WorkoutCreatorMetadataProps {
  attribution?: WorkoutAttribution | null;
  created_at?: string | null;
  created_by_username?: string | null;
  fallbackLabel?: string;
  className?: string;
}

export function WorkoutCreatorMetadata({
  attribution,
  created_at,
  created_by_username,
  fallbackLabel = 'ErgShoot HQ',
  className,
}: WorkoutCreatorMetadataProps) {
  const username = created_by_username?.trim();
  const creatorLabel = username
    ? username.startsWith('@')
      ? username
      : `@${username}`
    : (attribution?.name ?? fallbackLabel);
  const createdAtLabel = formatWorkoutCreatedAt(created_at);

  if (attribution === null || (!created_at && !username)) return null;

  return (
    <p
      className={cn('text-xs text-muted-foreground', className)}
      data-testid="workout-creator-metadata"
    >
      Created by {creatorLabel}
      {createdAtLabel && (
        <>
          <span aria-hidden="true"> · </span>
          <time dateTime={created_at ?? undefined}>{createdAtLabel}</time>
        </>
      )}
    </p>
  );
}

function formatWorkoutCreatedAt(value?: string | null): string | null {
  if (!value) return null;

  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  const date = new Date(hasTimezone ? normalized : `${normalized}Z`);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
